import { API_BASE_URL, DEFAULT_COMPANY_ID } from './config';
import { apiFetch } from './apiFetch';

export type CompetencyCategoryDto = {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CompetencyDto = {
  id: string;
  companyId: string;
  categoryId: string;
  code: string;
  name: string;
  description: string;
  levelMap: Record<string, string>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export async function listCompetencyCategories(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/competency-categories/company/${companyId}`);
  return response.json() as Promise<{
    success: boolean;
    data?: { categories: CompetencyCategoryDto[] };
    error?: string;
  }>;
}

export async function createCompetencyCategory(input: {
  companyId?: string;
  name: string;
  description?: string;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/competency-categories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      name: input.name,
      description: input.description,
    }),
  });

  return response.json() as Promise<{
    success: boolean;
    data?: { category: CompetencyCategoryDto };
    error?: string;
  }>;
}

export async function listCompetencies(input: { companyId?: string; categoryId?: string; query?: string }) {
  const params = new URLSearchParams();
  if (input.categoryId) {
    params.set('categoryId', input.categoryId);
  }
  if (input.query) {
    params.set('q', input.query);
  }

  const companyId = input.companyId ?? DEFAULT_COMPANY_ID;
  const url = `${API_BASE_URL}/api/competencies/company/${companyId}${params.size > 0 ? `?${params.toString()}` : ''}`;

  const response = await apiFetch(url);
  return response.json() as Promise<{
    success: boolean;
    data?: { competencies: CompetencyDto[] };
    error?: string;
  }>;
}

export async function updateCompetencyCategory(
  categoryId: string,
  input: { companyId?: string; name: string; description?: string },
) {
  const response = await apiFetch(`${API_BASE_URL}/api/competency-categories/${categoryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      name: input.name,
      description: input.description,
    }),
  });
  return response.json() as Promise<{
    success: boolean;
    data?: { category: CompetencyCategoryDto };
    error?: string;
  }>;
}

export async function deleteCompetencyCategory(categoryId: string, companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/competency-categories/${categoryId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return response.json() as Promise<{ success: boolean; error?: string }>;
}

export async function updateCompetency(
  competencyId: string,
  input: {
    companyId?: string;
    code: string;
    name: string;
    description?: string;
    levelMap?: Record<string, string>;
  },
) {
  const response = await apiFetch(`${API_BASE_URL}/api/competencies/${competencyId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      code: input.code,
      name: input.name,
      description: input.description,
      levelMap: input.levelMap,
    }),
  });
  return response.json() as Promise<{
    success: boolean;
    data?: { competency: CompetencyDto };
    error?: string;
  }>;
}

export async function deleteCompetency(competencyId: string, companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/competencies/${competencyId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return response.json() as Promise<{ success: boolean; error?: string }>;
}

export async function createCompetency(input: {
  companyId?: string;
  categoryId: string;
  code: string;
  name: string;
  description?: string;
  levelMap?: Record<string, string>;
}) {
  const response = await apiFetch(`${API_BASE_URL}/api/competencies`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      categoryId: input.categoryId,
      code: input.code,
      name: input.name,
      description: input.description,
      levelMap: input.levelMap,
    }),
  });

  return response.json() as Promise<{
    success: boolean;
    data?: { competency: CompetencyDto };
    error?: string;
  }>;
}
